import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { DeleteForever } from '@mui/icons-material';
import { useDropzone, DropEvent, FileRejection, FileWithPath } from 'react-dropzone';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';

/**
 * Props que o componente FileUploader recebe do "pai":
 */
interface FileUploaderProps {
  // Quando o usuário soltar (drop) novos arquivos
  onDrop: (accepted: File[], rejected: FileRejection[], event: DropEvent) => void;
  // A lista atual de arquivos aceitos (gerenciada pelo componente pai)
  acceptedFiles: File[];
  // A lista de arquivos rejeitados
  rejectedFiles: FileRejection[];
  // Função para limpar todos os arquivos
  handleClearAll: () => void;
  // Remove um arquivo específico
  handleRemoveFile: (file: File) => void;
  // Remove um arquivo rejeitado específico
  handleRemoveRejectedFile: (file: FileWithPath) => void;
}

/**
 * Reordena elementos no array, conforme o react-beautiful-dnd pede.
 */
function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onDrop,
  acceptedFiles,
  rejectedFiles,
  handleClearAll,
  handleRemoveFile,
  handleRemoveRejectedFile
}) => {

  // *** react-dropzone ***
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop, 
    accept: { 'application/pdf': ['.pdf'] }, // somente PDFs
    multiple: true,
    maxSize: 120 * 1024 * 1024, // 20 MB
  });

  // Mantemos um estado local para exibir e reordenar a lista de arquivos aceitos
  const [localFiles, setLocalFiles] = useState<File[]>(acceptedFiles);

  /**
   * Sempre que a lista `acceptedFiles` vinda das props mudar 
   * (por ex., o componente pai adicionou algo, limpou, etc.),
   * atualizamos nosso estado local. Isso garante a ordem inicial
   * de upload e reflete mudanças externas.
   */
  useEffect(() => {
    setLocalFiles(acceptedFiles);
  }, [acceptedFiles]);

  /**
   * Handler principal do react-beautiful-dnd para quando o usuário solta
   * (end) um item que estava sendo arrastado.
   */
  const onDragEnd = useCallback((result: DropResult) => {
    // Se o usuário dropou fora de qualquer Droppable, ou não moveu
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    // Reordena a lista local
    const newOrder = reorder(localFiles, result.source.index, result.destination.index);
    setLocalFiles(newOrder);
    // Caso precise notificar o pai, crie um callback (ex.: onReorder(newOrder))
  }, [localFiles]);

  // Indica se há qualquer arquivo (aceito ou rejeitado)
  const hasFiles = localFiles.length > 0 || rejectedFiles.length > 0;

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed #6C1E8F',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        cursor: 'pointer',
        textAlign: 'center',
        position: 'relative',
        justifyContent: hasFiles ? 'flex-start' : 'center',
        alignItems: hasFiles ? 'stretch' : 'center'
      }}
      /**
       * Impede que os eventos de arraste/disparo do DnD
       * reativem a lógica interna do Dropzone
       */
      onDragOver={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onDrop={(e) => {
        // se estivermos arrastando um item interno,
        // evitamos a reabertura do "File Explorer"
        e.stopPropagation();
      }}
    >
      <input {...getInputProps()} />

      {/* Mensagem inicial, se não há arquivos */}
      {!hasFiles && (
        <>
          {isDragActive ? (
            <Typography variant="body1" color="primary" sx={{ mb: 2 }}>
              Solte os arquivos PDF aqui...
            </Typography>
          ) : (
            <Typography variant="body1" sx={{ mb: 2 }}>
              Arraste e solte arquivos PDF aqui, ou clique para selecioná-los
            </Typography>
          )}
        </>
      )}

      {isDragReject && !hasFiles && (
        <Typography variant="body1" color="error" sx={{ mb: 2 }}>
          Alguns arquivos não são válidos. Somente PDFs são aceitos.
        </Typography>
      )}

      {/* Se há arquivos (aceitos e/ou rejeitados) */}
      {hasFiles && (
        <>
          {/* Botão (ícone) para limpar todos */}
          <Box sx={{ position: 'absolute', top: '8px', right: '8px' }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
                setLocalFiles([]);
              }}
              color="error"
            >
              <DeleteForever fontSize="small" color="inherit" />
            </IconButton>
          </Box>

          {/* Conteúdo principal: a lista de aceitos e rejeitados */}
          <Box sx={{ flex: '1 1 auto', overflowY: 'auto', mt: 2 }}>
            {/* Lista de aceitos: utilizamos react-beautiful-dnd */}
            <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left' }}>
              Arquivos aceitos (arraste para reordenar):
            </Typography>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable-accepted">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {localFiles.map((file, index) => (
                      <Draggable
                        key={`${file.name}-${index}`} 
                        draggableId={`${file.name}-${index}`}
                        index={index}
                      >
                        {(draggableProvided, snapshot) => (
                          <Box
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                              border: '1px solid #ddd',
                              borderRadius: 1,
                              p: 1,
                              backgroundColor: snapshot.isDragging ? '#fafafa' : '#fff',
                            }}
                            /**
                             * Impede que o arrastar do Draggable reabra
                             * o "File Explorer" do Dropzone
                             */
                            onDragStart={(e) => {
                              e.stopPropagation();
                            }}
                            onDragOver={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            onDrop={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              {file.name}
                            </Typography>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                handleRemoveFile(file);
                                setLocalFiles((prev) => prev.filter((f) => f !== file));
                              }}
                            >
                              <DeleteForever fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>

            {/* Lista de rejeitados */}
            {rejectedFiles.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, color: 'error.main', textAlign: 'left' }}
                >
                  Arquivos rejeitados:
                </Typography>
                {rejectedFiles.map((fr, idx) => (
                  <Box
                    key={`${fr.file.name}-${idx}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                      border: '1px solid #f44336',
                      borderRadius: 1,
                      p: 1
                    }}
                  >
                    <Typography variant="body2" color="error" sx={{ wordBreak: 'break-all' }}>
                      {fr.file.name} ({fr.errors.map((err) => err.message).join(', ')})
                    </Typography>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveRejectedFile(fr.file);
                      }}
                    >
                      <DeleteForever fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default FileUploader;